import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface PerformanceOptimizerProps {
  criticalCSS?: string;
  preloadFonts?: string[];
  preloadImages?: string[];
  dnsPrefetch?: string[];
}

/**
 * PerformanceOptimizer component for Core Web Vitals optimization
 * Implements resource hints, preloading, and performance optimizations
 */
const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({
  criticalCSS,
  preloadFonts = [],
  preloadImages = [],
  dnsPrefetch = []
}) => {
  useEffect(() => {
    // Optimize loading performance
    const optimizePerformance = () => {
      // Preload critical resources
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          // Preload non-critical resources during idle time
          preloadFonts.forEach(font => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = font;
            link.as = 'font';
            link.type = 'font/woff2';
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
          });
        });
      }
    };

    optimizePerformance();
  }, [preloadFonts]);

  return (
    <Helmet>
      {/* DNS Prefetch for external domains */}
      {dnsPrefetch.map(domain => (
        <link key={domain} rel="dns-prefetch" href={domain} />
      ))}
      
      {/* Preconnect to critical domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://qualityaivoice.com" />
      
      {/* Preload critical images */}
      {preloadImages.map(image => (
        <link key={image} rel="preload" as="image" href={image} />
      ))}
      
      {/* Critical CSS inline */}
      {criticalCSS && (
        <style type="text/css">{criticalCSS}</style>
      )}
      
      {/* Resource hints for better performance */}
      <link rel="preload" href="/src/main.tsx" as="script" />
      
      {/* Optimize font loading */}
      <link 
        rel="preload" 
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" 
        as="style" 
        onLoad="this.onload=null;this.rel='stylesheet'"
      />
      
      {/* Performance monitoring */}
      <script>
        {`
          // Monitor Core Web Vitals
          if ('PerformanceObserver' in window) {
            const observer = new PerformanceObserver((list) => {
              for (const entry of list.getEntries()) {
                if (entry.entryType === 'largest-contentful-paint') {
                  console.log('LCP:', entry.startTime);
                }
                if (entry.entryType === 'first-input') {
                  console.log('FID:', entry.processingStart - entry.startTime);
                }
                if (entry.entryType === 'layout-shift') {
                  console.log('CLS:', entry.value);
                }
              }
            });
            
            observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
          }
        `}
      </script>
    </Helmet>
  );
};

export default PerformanceOptimizer; 